import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import SessionsDetailModalSectionTitle from './SessionsDetailModalSectionTitle';
import InputField from '../../InputField';
import SessionsFormField from '../SessionsFormField';
import Button from '../../Button';

const SessionsDetailModalTitle = ({
  title,
  placeholder,
  onSubmit,
  isEditable,
  onEdit,
  onCancel,
  disabled,
  composing,
  loading,
}) => {
  const [internalComposing, setInternalComposing] = useState(false);
  const { t } = useTranslation('components');
  const titleInputRef = useRef();
  const {
    register,
    handleSubmit: wrapSubmit,
    errors,
  } = useForm({
    resolver: yupResolver(
      yup.object().shape({
        title: yup.string().required(t('global:validation.messages.required')),
      }),
    ),
  });
  const activeComposingState = typeof composing !== 'undefined' ? composing : internalComposing;

  const handleEdit = () => {
    setInternalComposing(true);
    onEdit();
  };

  const handleCancel = () => {
    setInternalComposing(false);
    titleInputRef.current.value = title;
    onCancel();
  };

  useEffect(() => {
    if (titleInputRef.current) {
      register(titleInputRef.current);
    }
  }, [titleInputRef]);

  useEffect(() => {
    if (titleInputRef && titleInputRef.current && activeComposingState) {
      titleInputRef.current.selectionStart = 0;
      titleInputRef.current.selectionEnd = titleInputRef.current.value.length;
      titleInputRef.current.focus();
    }
  }, [activeComposingState, titleInputRef]);

  const handleSubmit = ({ title: value }, event) => {
    onSubmit(value, event);
  };

  return (
    <section className="mb-6" data-component="SessionsDetailModalTitle">
      <SessionsDetailModalSectionTitle onEdit={handleEdit} isEditable={isEditable}>
        {t('Sessions.SessionsDetailModal.SessionsDetailModalTitle.title')}
      </SessionsDetailModalSectionTitle>
      <div hidden={!activeComposingState} className="p-1">
        <form onSubmit={wrapSubmit(handleSubmit)}>
          <SessionsFormField error={errors.title}>
            <InputField
              name="title"
              ref={titleInputRef}
              defaultValue={title}
              placeholder={placeholder}
              state={errors.title ? 'invalid' : 'default'}
            />
          </SessionsFormField>
          <div className="flex justify-end mt-4 text-sm space-x-4">
            <Button onClick={handleCancel} kind="secondary:ghost">
              {t('Sessions.SessionsDetailModal.SessionsDetailModalTitle.close')}
            </Button>
            <Button isDisabled={disabled} isLoading={loading} type="submit">
              {t('Sessions.SessionsDetailModal.SessionsDetailModalTitle.save')}
            </Button>
          </div>
        </form>
      </div>
      {!activeComposingState && (
        <button
          type="button"
          className="mb-2 select-none focus:outline-none cursor-text"
          onClick={handleEdit}
        >
          {title || <span className="text-slate-500">{placeholder}</span>}
        </button>
      )}
    </section>
  );
};

SessionsDetailModalTitle.propTypes = {
  title: PropTypes.string,
  placeholder: PropTypes.string,
  onSubmit: PropTypes.func,
  isEditable: PropTypes.bool,
  disabled: PropTypes.bool,
  onEdit: PropTypes.func,
  onCancel: PropTypes.func,
  composing: PropTypes.bool,
  loading: PropTypes.bool,
};

SessionsDetailModalTitle.defaultProps = {
  onSubmit: () => {},
  onEdit: () => {},
  onCancel: () => {},
  loading: false,
};

export default SessionsDetailModalTitle;
